import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { initialPaging } from "../Constants/paging";
import {
  CallApiGetVoucher,
  CallApiCreateVoucher,
  CallApiDeleteVoucher,
  CallApiUpdateVoucher,
} from "../Api/Enforcement";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { SwalError, SwalSuccess, SwalFail } from "../Share/Swal";
import { VOUCHER_TYPES } from "../Constants/voucher";

export const useAdminVoucherPage = () => {
  const current = dayjs().startOf("month");
  const [vouchers, setVouchers] = useState([]);
  const [paging, setPaging] = useState(initialPaging);
  const pagingRef = useRef(paging);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [code, setCode] = useState(null);

  const [type, setType] = useState({ value: false, label: "NOT" });

  useEffect(() => {
    pagingRef.current = paging;
  }, [paging]);

  const getVouchers = useCallback(
    (pageNumber) => {
      const data = {
        searchText: search,
        page:
          pageNumber !== undefined ? pageNumber : pagingRef.current.pageIndex,
        isDeleted: type.value,
      };

      CallApiGetVoucher(data, (res) => {
        if (res.Status === "success") {
          var data = res.Response.data;
          setVouchers(data.Items);
          setPaging((prev) => ({
            ...prev,
            next: data.Next,
            previous: data.Previous,
            totalPages: data.TotalPagesCount,
            pageIndex: data.PageIndex,
          }));
        }
      });
    },
    [search, type]
  );

  useEffect(() => {
    getVouchers();
  }, [getVouchers]);

  //#region Add voucher
  const createSchemaForm = yup.object().shape({
    quantity: yup
      .number()
      .typeError("Số lượng phải là một số lớn hơn 0")
      .required("Vui lòng nhập số lượng")
      .min(1, "Số lượng phải lớn hơn 0"),
    limit1: yup
      .number()
      .typeError("Hạn mức 1 là một số lớn hơn 0")
      .required("Vui lòng nhập hạn mức 1")
      .min(0.01, "Hạn mức 1 phải lớn hơn 0")
      .test(
        "is-greater-than-min-pay",
        "Hạn mức 1 lớn hơn Mức thêm tối thiểu",
        function (value) {
          const { min_pay } = this.parent;
          return value >= (min_pay || 0);
        }
      ),
    ex_rate1: yup
      .number()
      .typeError("Hệ số 1 một số lớn hơn 0")
      .required("Vui lòng nhập hệ số 1")
      .min(0.01, "Hệ số 1 phải lớn hơn 0"),
    limit2: yup
      .number()
      .typeError("Hạn mức 2 là một số lớn hơn 0")
      .required("Vui lòng nhập hạn mức 2")
      .min(0.01, "Hạn mức 2 phải lớn hơn 0")
      .test(
        "is-greater-than-min-pay",
        "Hạn mức 2 lớn hơn Mức thêm tối thiểu",
        function (value) {
          const { min_pay } = this.parent;
          return value >= (min_pay || 0);
        }
      ),
    ex_rate2: yup
      .number()
      .typeError("Hệ số 2 một số lớn hơn 0")
      .required("Vui lòng nhập hệ số 2")
      .min(0.01, "Hệ số 2 phải lớn hơn 0"),
    min_pay: yup
      .number()
      .typeError("Mức thêm tối thiểu phải là một số lớn hơn 0")
      .required("Vui lòng nhập Mức thêm tối thiểu phải")
      .min(0.01, "Mức thêm tối thiểu phải lớn hơn 0"),
    ref_rate: yup
      .number()
      .typeError("Mức thưởng tối thiểu phải là một số lớn hơn hoặc bằng 0")
      .required("Vui lòng nhập Mức thưởng tối thiểu phải")
      .min(0, "Mức thưởng tối thiểu phải lớn hơn 0"),
  });

  const { control, handleSubmit, reset, getValues } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        month: current.month() + 1,
        year: current.year(),
        quantity: 1,
        limit1: 1,
        ex_rate1: 1,
        limit2: 1,
        ex_rate2: 1,
        min_pay: 1,
        voucher_type: VOUCHER_TYPES[0].value,
        ref_rate: 0,
      }),
      [current]
    ),
    resolver: yupResolver(createSchemaForm),
  });

  const handleSetYear = (year) => {
    reset({ ...getValues(), year });
  };

  const handleSetMonth = (month) => {
    reset({ ...getValues(), month });
  };

  const handleReset = useCallback(() => {
    reset({
      month: current.month() + 1,
      year: current.year(),
      quantity: 1,
      limit1: 1,
      ex_rate1: 1,
      limit2: 1,
      ex_rate2: 1,
      min_pay: 1,
      voucher_type: VOUCHER_TYPES[0].value,
      ref_rate: 0,
    });
  }, [current, reset]);

  const handleCloseCreateModal = useCallback(() => {
    setCode(null);
    setShowCreateModal(false);
    handleReset();
  }, [setCode, setShowCreateModal, handleReset]);

  const handleShowCreateModal = useCallback(() => {
    setShowCreateModal(true);
    setCode(null);
    handleReset();
  }, [setShowCreateModal, handleReset, setCode]);

  const handleShowUpdateModal = useCallback(
    (value) => {
      if (!(value.voucher_type === VOUCHER_TYPES[0].value)) {
        setCode(value.voucher_code);
        setShowCreateModal(true);
        reset({
          month: value.month,
          year: value.year,
          quantity: 1,
          limit1: value.limit1,
          ex_rate1: value.ex_rate1,
          limit2: value.limit2,
          ex_rate2: value.ex_rate2,
          min_pay: value.min_pay,
          voucher_type: value.voucher_type,
          ref_rate: value.ref_rate,
        });
      }
    },
    [setShowCreateModal, setCode, reset]
  );

  const handleCreate = useCallback(
    (formData) => {
      setLoading(true);
      if (loading) return;

      CallApiCreateVoucher(formData, (res) => {
        setLoading(false);
        if (res === "success") {
          reset();
          SwalSuccess("Cập nhật thành công!");
          handleCloseCreateModal();
          getVouchers(0);
        } else {
          var error = res.Status;
          if (error.status === 400) SwalFail(error.data.Message);
          else
            SwalError(
              "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ"
            );
        }
      });
    },
    [loading, reset, handleCloseCreateModal, getVouchers]
  );

  const handleUpdate = useCallback(
    (formData) => {
      setLoading(true);
      if (loading) return;

      CallApiUpdateVoucher(code, formData, (res) => {
        setLoading(false);
        if (res === "success") {
          reset();
          SwalSuccess("Cập nhật thành công!");
          handleCloseCreateModal();
          getVouchers(0);
        } else {
          var error = res.Status;
          if (error.status === 400) SwalFail(error.data.Message);
          else
            SwalError(
              "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ"
            );
        }
      });
    },
    [loading, reset, handleCloseCreateModal, getVouchers, code]
  );

  const handleSubmitForm = useCallback(
    (formData) => {
      if (code) {
        handleUpdate(formData);
      } else {
        handleCreate(formData);
      }
    },
    [code, handleCreate, handleUpdate]
  );
  //#endregion

  const handleSelectType = useCallback((type) => {
    setType(type);
    setPaging((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  function formatMinPay(number) {
    var tranfer = number * 1000000;
    return tranfer.toLocaleString("vi-VN");
  }

  const formatMonthYear = (item, key) => {
    if (key.name === "month") {
      const month = item.month || "00";
      const year = item.year || "0000";
      return `${month}/${year}`;
    }
  };

  const handleDelete = (id) => {
    setLoading(true);

    if (loading) return;

    CallApiDeleteVoucher(id, (res) => {
      setLoading(false);
      if (res === "success") {
        reset();
        SwalSuccess("Cập nhật thành công!");
        getVouchers(0);
      } else {
        var error = res.Status;
        if (error.status === 400) SwalFail(error.data.Message);
        else
          SwalError("Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ");
      }
    });
  };

  const formatRate = (number) => {
    if (!number) return "";
    return `${number * 100} %`;
  };

  return {
    search,
    vouchers,
    paging,
    control,
    showCreateModal,
    type,
    handleCloseCreateModal,
    handleShowCreateModal,
    handleSubmit: handleSubmit(handleSubmitForm),
    setSearch,
    getVouchers,
    handleSetYear,
    handleSetMonth,
    handleSelectType,
    formatMinPay,
    formatMonthYear,
    handleDelete,
    handleShowUpdateModal,
    formatRate,
  };
};
